import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePageTitle } from '../../contexts/PageTitleContext';
import { useUnsavedChanges } from '../../contexts/UnsavedChangesContext';
import { useUserPreferences } from '../../hooks/useUserPreferences';
import NotificationCenter from '../NotificationCenter';
import GlobalSearchModal from '../GlobalSearchModal';
import { LanguageSelectorModal } from '../LanguageSelector';
import {
  UserIcon,
  ChevronDownIcon,
  ArrowRightOnRectangleIcon,
  Cog6ToothIcon,
  ArrowRightIcon,
  PlusIcon,
  PencilIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  MoonIcon,
  SunIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';
import { useDarkMode } from '../../contexts/DarkModeContext';
import { usePolicyViewHeader } from '../../contexts/PolicyViewHeaderContext';
import { useTranslation } from 'react-i18next';

type ViewMode = 'user' | 'admin' | 'sysadmin';

const Header: React.FC = () => {
  const { t } = useTranslation(['common', 'companies']);
  const { profile } = useUser();
  const { logout, user } = useAuth();
  const isAuditor = user?.role === 'company_auditor' || user?.role === 'system_auditor';
  // Reader-mode end-users see no Settings link in the user menu — same
  // rationale as Sidebar.tsx (`isReader` branch of `bottomNavigation`):
  // the Settings page is full-mode only. Admins who happen to be in
  // reader mode (e.g. suspended company) still see Settings.
  const isReader = user?.mode === 'reader'
    && user?.role !== 'admin'
    && user?.role !== 'sysadmin';
  const { title, description, customContent } = usePageTitle();
  const policyViewHeader = usePolicyViewHeader();
  const policyViewShowEditButton = policyViewHeader?.showEditPolicyButton ?? true;
  const { hasUnsavedChanges, onSave, isSubmitting } = useUnsavedChanges();
  const { preferences } = useUserPreferences();
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const location = useLocation();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  
  // Track view mode for conditional rendering
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    try {
      const saved = localStorage.getItem('view_mode');
      if (saved) {
        const mode = saved as ViewMode;
        if (user?.role === 'sysadmin') {
          return mode;
        } else if (user?.role === 'admin') {
          return mode === 'sysadmin' ? 'admin' : mode;
        }
        return 'user';
      }
      if (user?.role === 'sysadmin') return 'sysadmin';
      if (user?.role === 'admin') return 'admin';
      return 'user';
    } catch {
      return 'user';
    }
  });
  
  // Listen for view mode changes
  useEffect(() => {
    const handler = (event: CustomEvent) => {
      if (event.type !== 'view-mode-changed') {
        return;
      }
      const newMode = event.detail?.viewMode;
      if (newMode && newMode !== viewMode) {
        setViewMode(newMode as ViewMode);
      }
    };
    window.addEventListener('view-mode-changed', handler as EventListener);
    return () => {
      window.removeEventListener('view-mode-changed', handler as EventListener);
    };
  }, [viewMode]);
  
  // Check if sidebar is collapsed from localStorage
  useEffect(() => {
    const checkSidebarState = () => {
      const saved = localStorage.getItem('sidebar_collapsed');
      const windowWidth = window.innerWidth;
      // Sidebar is collapsed if manually set to true, or if window is < 1280px and not manually set to false
      // This matches the logic in Sidebar.tsx
      const isManuallyCollapsed = saved ? JSON.parse(saved) : null;
      const collapsed = isManuallyCollapsed !== null ? isManuallyCollapsed : windowWidth < 1280;
      setIsSidebarCollapsed(collapsed);
    };
    
    checkSidebarState();
    // Listen for storage changes (when sidebar state changes in another tab)
    window.addEventListener('storage', checkSidebarState);
    // Also check on resize
    window.addEventListener('resize', checkSidebarState);
    
    // Custom event listener for sidebar collapse state changes (same tab)
    const handleSidebarToggle = () => checkSidebarState();
    window.addEventListener('sidebar-toggle', handleSidebarToggle);
    
    return () => {
      window.removeEventListener('storage', checkSidebarState);
      window.removeEventListener('resize', checkSidebarState);
      window.removeEventListener('sidebar-toggle', handleSidebarToggle);
    };
  }, []);
  
  // Listen for preview open/close events to track preview state
  useEffect(() => {
    const handlePreviewOpen = () => setIsPreviewOpen(true);
    const handlePreviewClose = () => setIsPreviewOpen(false);
    
    window.addEventListener('watermark-preview-opened', handlePreviewOpen);
    window.addEventListener('watermark-preview-closed', handlePreviewClose);
    window.addEventListener('open-watermark-preview', handlePreviewOpen);
    window.addEventListener('close-watermark-preview', handlePreviewClose);
    
    return () => {
      window.removeEventListener('watermark-preview-opened', handlePreviewOpen);
      window.removeEventListener('watermark-preview-closed', handlePreviewClose);
      window.removeEventListener('open-watermark-preview', handlePreviewOpen);
      window.removeEventListener('close-watermark-preview', handlePreviewClose);
    };
  }, []);

  // Check if web notifications are enabled (default to true if preferences not loaded yet)
  const webNotificationsEnabled = preferences?.web_notifications !== false;

  // Handle click outside and Escape key to close user menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isUserMenuOpen]);

  const handleNavigation = (href: string, name: string) => {
    console.log('🔍 HEADER DEBUG: Navigation clicked');
    console.log('  - href:', href);
    console.log('  - name:', name);
    console.log('  - current location:', location.pathname);
    console.log('  - will navigate to:', href);

    // Force navigation by updating window location
    if (href !== location.pathname) {
      console.log('  - forcing navigation to:', href);
      window.location.href = href;
    } else {
      console.log('  - already on this page, no navigation needed');
    }
  };

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
      setIsLoggingOut(false);
    }
  };


  // Determine which pages should show the search bar in header
  // Assets and Notifications pages have their own filter buttons, so don't show search bar
  const isAssetsPage = location.pathname === '/assets';
  const isNotificationsPage = location.pathname === '/notifications';
  const showHeaderSearchBar = false; // Disabled - Assets and Notifications have their own filter buttons

  // Show create policy button only on policies page
  const isPoliciesPage = location.pathname === '/policies';
  const isFoldersPage = location.pathname.startsWith('/folders');

  // Show add company button only on companies list page (not on new or edit pages)
  const isCompaniesListPage = location.pathname === '/clients';
  
  // Show create user button only on users list page (not on detail pages)
  const isUsersPage = location.pathname === '/users';

  // Show edit policy button only on policy view page (not on edit page or new page)
  const isPolicyViewPage = location.pathname.match(/^\/policies\/[^/]+$/) && !location.pathname.includes('/edit') && !location.pathname.includes('/new');

  // Show dark mode toggle on settings page
  const isSettingsPage = location.pathname === '/settings';
  
  // Show preview button on watermark settings pages
  const isWatermarkSettingsPage = location.pathname === '/administration/watermarks';
  const isSettingsWatermarkTab = isSettingsPage && (() => {
    try {
      const saved = localStorage.getItem('settings_activeTab');
      return saved === 'watermark';
    } catch {
      return false;
    }
  })();
  const showWatermarkPreview = isWatermarkSettingsPage || isSettingsWatermarkTab;


  return (
    <>
      {/* Logout Loading Overlay */}
      {isLoggingOut && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg p-8 flex flex-col items-center space-y-4 shadow-2xl">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600"></div>
            <p className="text-lg font-medium text-gray-900">Logging out...</p>
          </div>
        </div>
      )}

      <header className="bg-white dark:bg-black shadow-sm border-b border-gray-200 dark:border-gray-900 transition-colors relative">
        <div className="px-4 py-1.5">
          <div className="flex items-center justify-between">
            {/* Left side - Title or Search Bar */}
            <div className="flex items-center space-x-4 flex-1">
              {showHeaderSearchBar ? (
                <div className="flex-1 max-w-2xl mx-auto">
                  <div
                    onClick={() => setIsSearchModalOpen(true)}
                    className="relative w-full cursor-pointer group"
                  >
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 group-hover:text-blue-500 transition-colors" />
                    </div>
                    <div className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg leading-5 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-500 focus:outline-none focus:bg-white dark:focus:bg-gray-800 focus:ring-2 focus:ring-blue-500 transition-all shadow-sm group-hover:shadow-md">
                      Search {isAssetsPage ? 'assets' : isNotificationsPage ? 'notifications' : ''}...
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {customContent?.customNode ? (
                    <div className="flex-1 min-w-0">
                      {customContent.customNode}
                    </div>
                  ) : customContent?.showTabs ? (
                    <nav className="flex space-x-2 md:space-x-4">
                      {customContent.tabs?.map((tab) => (
                        <button
                          key={tab.id}
                          onClick={() => customContent.onTabChange?.(tab.id)}
                          className={`py-4 px-2 md:px-1 border-b-2 font-medium text-sm flex items-center justify-center md:justify-start space-x-2 ${customContent.activeTab === tab.id
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                          title={tab.label}
                        >
                          {tab.icon}
                          <span className="hidden md:inline">{tab.label}</span>
                        </button>
                      ))}
                    </nav>
                  ) : (
                    title && !isSidebarCollapsed && (
                      <div className="hidden xl:block flex-1 min-w-0">
                        <h1 className="text-xl font-semibold text-gray-900 dark:text-white truncate">{title}</h1>
                        {description && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 truncate">{description}</p>
                        )}
                      </div>
                    )
                  )}
                </>
              )}
            </div>

            <div className="flex items-center gap-1">
              {/* Translation page buttons - show if customContent.rightSideButtons is provided */}
              {customContent?.rightSideButtons}
              
              {/* Add Company Button - only show on companies list page, for sysadmins, and in sysadmin mode */}
              {isCompaniesListPage && user?.role === 'sysadmin' && viewMode === 'sysadmin' && (
                <Link
                  to="/clients/new"
                  className="btn-create-policy flex items-center space-x-2"
                >
                  <PlusIcon className="w-5 h-5" />
                  <span className="hidden xl:inline">{t('companies:newOrganisation')}</span>
                </Link>
              )}

              {/* Create Policy Button - only show on policies page */}
              {isPoliciesPage && (
                <Link
                  to="/policies/new"
                  className="btn-create-policy flex items-center space-x-2"
                >
                  <PlusIcon className="w-5 h-5" />
                  <span className="hidden xl:inline">New Policy</span>
                </Link>
              )}

              {/* Create User Button - only show on users page and for sysadmins */}
              {isUsersPage && user?.role === 'sysadmin' && (
                <button
                  onClick={() => {
                    // Dispatch custom event to open create user modal
                    window.dispatchEvent(new CustomEvent('open-create-user-modal'));
                  }}
                  className="btn-create-policy flex items-center space-x-2"
                >
                  <PlusIcon className="w-5 h-5" />
                  <span className="hidden xl:inline">New User</span>
                </button>
              )}

              {/* Edit Policy Button - only show on policy view page when user can edit this policy type */}
              {isPolicyViewPage && policyViewShowEditButton && (
                <Link
                  to={`${location.pathname}/edit`}
                  className="btn-create-policy flex items-center space-x-2"
                >
                  <PencilIcon className="w-5 h-5" />
                  <span>Edit</span>
                </Link>
              )}

              {/* Add Folder Button — web creates ORG TEMPLATES only, so show only in
                  admin/sysadmin view mode (Standard/user mode creates nothing here). */}
              {isFoldersPage && (user?.role === 'admin' || user?.role === 'sysadmin') && viewMode !== 'user' && (
                <button
                  onClick={() => {
                    window.dispatchEvent(new CustomEvent('open-create-folder'));
                  }}
                  className="btn-create-policy flex items-center space-x-2"
                >
                  <PlusIcon className="w-5 h-5" />
                  <span className="hidden xl:inline">Add Folder</span>
                </button>
              )}

              {/* Watermark Preview Button - show on watermark settings pages */}
              {showWatermarkPreview && (
                <button
                  onClick={() => {
                    if (isPreviewOpen) {
                      window.dispatchEvent(new CustomEvent('close-watermark-preview'));
                    } else {
                      window.dispatchEvent(new CustomEvent('open-watermark-preview'));
                    }
                  }}
                  className="btn-create-policy flex items-center space-x-2 rounded-lg"
                >
                  <EyeIcon className="w-5 h-5" />
                  <span className="hidden md:inline">{isPreviewOpen ? 'Close Preview' : 'Preview'}</span>
                </button>
              )}

              {/* Save Button - only show when there are unsaved changes */}
              {hasUnsavedChanges && onSave && (
                <button
                  onClick={onSave}
                  disabled={isSubmitting}
                  className="btn-encrypt flex items-center space-x-2"
                >
                  <span className="hidden md:inline">{isSubmitting ? 'Saving...' : 'Save'}</span>
                  <ArrowRightIcon className="w-4 h-4" />
                </button>
              )}


              {/* Notification Center and User Menu - grouped together */}
              <div className="flex items-center gap-0.5">
                {/* Notification Center - only show if web notifications are enabled */}
                {webNotificationsEnabled && (
                  <NotificationCenter
                    baseUrl={process.env.REACT_APP_API_URL || ''}
                    token={''} // 🔒 Empty - WebSocket will use cookies
                    userId={profile?.id || ''}
                    deviceId={''} // 🔒 Empty - backend reads from cookie
                  />
                )}

                {/* User Menu */}
                <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center space-x-1.5 p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-900 transition-colors"
                >
                  <div className="w-8 h-8 bg-governate-100 dark:bg-governate-900 rounded-full flex items-center justify-center">
                    <UserIcon className="w-5 h-5 text-governate-600 dark:text-governate-400" />
                  </div>
                  <div className="text-left hidden xl:block">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {profile?.name || 'User'}
                    </p>
                  </div>
                  <ChevronDownIcon className="w-4 h-4 text-gray-400 dark:text-gray-500 hidden xl:block" />
                </button>

                {/* Dropdown Menu */}
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-black rounded-md shadow-lg border border-gray-200 dark:border-gray-800 z-50">
                    <div className="py-1">
                      <div
                        onClick={() => {
                          handleNavigation('/profile', t('header.account'));
                          setIsUserMenuOpen(false);
                        }}
                        className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-900 cursor-pointer"
                      >
                        <UserIcon className="w-4 h-4 mr-3" />
                        {t('header.account')}
                      </div>
                      {!isAuditor && !isReader && (
                        <div
                          onClick={() => {
                            handleNavigation('/settings', t('header.settings'));
                            setIsUserMenuOpen(false);
                          }}
                          className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-900 cursor-pointer"
                        >
                          <Cog6ToothIcon className="w-4 h-4 mr-3" />
                          {t('header.settings')}
                        </div>
                      )}
                      <hr className="my-1 border-gray-200 dark:border-gray-700" />
                      {/* Dark Mode Toggle - icon only */}
                      <button
                        onClick={() => {
                          toggleDarkMode();
                          setIsUserMenuOpen(false);
                        }}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-900"
                        aria-label={t('header.ariaSwitchMode', { mode: isDarkMode ? t('header.lightMode') : t('header.darkMode') })}
                      >
                        {isDarkMode ? (
                          <SunIcon className="w-4 h-4 mr-3 text-yellow-500" />
                        ) : (
                          <MoonIcon className="w-4 h-4 mr-3 text-blue-400" />
                        )}
                        {isDarkMode ? t('header.lightMode') : t('header.darkMode')}
                      </button>
                      {/* Language Selector */}
                      <button
                        onClick={() => {
                          setIsLanguageModalOpen(true);
                          setIsUserMenuOpen(false);
                        }}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-900"
                        aria-label={t('header.ariaSelectLanguage')}
                      >
                        <GlobeAltIcon className="w-4 h-4 mr-3 text-gray-500 dark:text-gray-400" />
                        {t('labels.language')}
                      </button>
                      <button
                        onClick={handleLogout}
                        className="flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-900"
                      >
                        <ArrowRightOnRectangleIcon className="w-4 h-4 mr-3" />
                        {t('header.logout')}
                      </button>
                    </div>
                  </div>
                )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Global Search Modal */}
      {isSearchModalOpen && (
        <GlobalSearchModal
          isOpen={isSearchModalOpen}
          onClose={() => setIsSearchModalOpen(false)}
          initialTab={isAssetsPage ? 'assets' : isNotificationsPage ? 'notifications' : undefined}
          singleTabMode={isAssetsPage || isNotificationsPage}
          initialFilterValues={(() => {
            // Read current filters from localStorage based on current page
            if (isAssetsPage) {
              try {
                const saved = localStorage.getItem('assets_currentFilters');
                if (saved) return JSON.parse(saved);
              } catch (e) {
                console.error('Failed to parse saved assets filters:', e);
              }
            } else if (isNotificationsPage) {
              try {
                const saved = localStorage.getItem('notifications_currentFilters');
                if (saved) return JSON.parse(saved);
              } catch (e) {
                console.error('Failed to parse saved notifications filters:', e);
              }
            }
            return undefined;
          })()}
        />
      )}

      {/* Language Selector Modal */}
      <LanguageSelectorModal
        isOpen={isLanguageModalOpen}
        onClose={() => setIsLanguageModalOpen(false)}
      />
    </>
  );
};

export default Header;
